import {
  addPolygonModeAtom,
  DEFAULT_ADD_POLYGON_MODE,
  meshPickTargetsAtom,
  pointCloudTargetsAtom,
} from '@/atoms/viewer.atom';
import { CrosshairMarker } from '@/components/3D/CrosshairMarker';
import { pickSurface } from '@/components/3D/pickSurface';
import { SegmentLabels } from '@/components/3D/SegmentLabels';
import { TrailingSegmentLine } from '@/components/3D/TrailingSegmentLine';
import { isPolylineSimple2D, isRingSimple2D } from '@/lib/ringSimple';
import { Line } from '@react-three/drei';
import { useThree, type ThreeEvent } from '@react-three/fiber';
import { useAtom, useAtomValue } from 'jotai';
import { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';

const POLYGON_COLOR = '#a855f7';
const POLYGON_COLOR_PREV = '#a855f799';
const ERROR_COLOR = '#dc2626';
const SNAP_HIGHLIGHT_COLOR = '#22c55e';

// Snap-to-first-vertex radius in screen pixels.
const SNAP_PX_THRESHOLD = 14;

type VertexAddress = { kind: 'current'; vertexIdx: number } | { kind: 'committed'; partIdx: number; vertexIdx: number };

const projectXY = (verts: Array<[number, number, number]>): Array<[number, number]> => verts.map(([x, y]) => [x, y]);

/**
 * In-canvas N-point picker for Polygon / MultiPolygon features.
 *
 * Click vertices to build the current ring; ≥3 vertices required to commit.
 * Hovering near the first vertex highlights it as a snap target — clicking
 * there auto-closes the ring (equivalent to Finalize). Self-intersection
 * is checked live: the candidate edge to the cursor renders red and the
 * click is rejected if appending the vertex would make the ring or
 * polyline non-simple.
 *
 * Picked vertices stay draggable: pointer-down on a marker drags it on a
 * camera-perpendicular plane, then snaps the final position to the surface
 * under the cursor on release.
 *
 * Simplicity is checked in world XY (top-down) projection. v1 is built for
 * roof / ground / top-down picks; near-vertical surfaces would need a per-ring
 * basis like GeoJson.tsx's editing-time check (deferred).
 */
export const AddPolygonTool = () => {
  const [mode, setMode] = useAtom(addPolygonModeAtom);
  const pcoTargets = useAtomValue(pointCloudTargetsAtom);
  const meshTargets = useAtomValue(meshPickTargetsAtom);
  const camera = useThree((state) => state.camera);
  const renderer = useThree((state) => state.gl);
  const raycaster = useMemo(() => new THREE.Raycaster(), []);

  const [hoverPoint, setHoverPoint] = useState<THREE.Vector3 | null>(null);
  const [candidateInvalid, setCandidateInvalid] = useState(false);

  const enabled = mode.enabled;
  const isPicking = enabled && mode.phase === 'picking';

  const draggingRef = useRef<{ address: VertexAddress; plane: THREE.Plane } | null>(null);
  // Timestamp-based suppression of the canvas click that follows a
  // vertex drag / marker click. A boolean flag could get stuck `true`
  // if the trailing canvas click event never arrives (browser/R3F
  // timing edge case), permanently swallowing the user's next click —
  // which presented as "I closed the polygon ring but I can't start a
  // new part". A timestamp auto-expires.
  const suppressClickUntilRef = useRef(0);
  // Latest snapshots of mutable inputs the click handler needs. Refs so
  // the click effect re-binds only on the isPicking flip, never during
  // a gesture.
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const pcoTargetsRef = useRef(pcoTargets);
  pcoTargetsRef.current = pcoTargets;
  const meshTargetsRef = useRef(meshTargets);
  meshTargetsRef.current = meshTargets;

  useEffect(() => {
    if (!enabled) {
      renderer.domElement.style.cursor = 'default';
      setHoverPoint(null);
      setCandidateInvalid(false);
    }
  }, [enabled, renderer]);

  const worldToScreenPx = (p: THREE.Vector3, rect: DOMRect): { x: number; y: number } | null => {
    const ndc = p.clone().project(camera);
    if (ndc.z < -1 || ndc.z > 1) return null;
    return {
      x: ((ndc.x + 1) / 2) * rect.width,
      y: ((1 - ndc.y) / 2) * rect.height,
    };
  };

  // Hover preview during picking. Snap-to-first + validity computed on each move.
  useEffect(() => {
    if (!isPicking) return;
    const dom = renderer.domElement;

    const onMove = async (e: MouseEvent) => {
      if (draggingRef.current) return;
      const m = modeRef.current;
      const rect = dom.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(new THREE.Vector2(x, y), camera);
      const hit = pickSurface(
        renderer,
        camera,
        raycaster,
        pcoTargetsRef.current,
        meshTargetsRef.current,
      );
      if (!hit) {
        setHoverPoint(null);
        setMode((prev) => (prev.snapToFirst ? { ...prev, snapToFirst: false } : prev));
        setCandidateInvalid(false);
        dom.style.cursor = 'default';
        return;
      }

      let snap = false;
      if (m.currentPart.length >= 3) {
        const first = m.currentPart[0];
        const firstVec = new THREE.Vector3(first[0], first[1], first[2]);
        const firstScreen = worldToScreenPx(firstVec, rect);
        if (firstScreen) {
          const dx = e.clientX - rect.left - firstScreen.x;
          const dy = e.clientY - rect.top - firstScreen.y;
          if (Math.sqrt(dx * dx + dy * dy) <= SNAP_PX_THRESHOLD) snap = true;
        }
      }

      const candidate: [number, number, number] = snap
        ? m.currentPart[0]
        : [hit.position.x, hit.position.y, hit.position.z];
      setHoverPoint(snap ? new THREE.Vector3(...candidate) : hit.position.clone());

      let invalid = false;
      if (snap) {
        invalid = !isRingSimple2D(projectXY(m.currentPart));
      } else if (m.currentPart.length >= 2) {
        const next = [...m.currentPart, candidate];
        invalid = !isPolylineSimple2D(projectXY(next));
      }
      setCandidateInvalid(invalid);
      setMode((prev) => (prev.snapToFirst === snap ? prev : { ...prev, snapToFirst: snap }));
      dom.style.cursor = snap ? 'pointer' : 'crosshair';
    };

    dom.addEventListener('pointermove', onMove);
    return () => {
      dom.removeEventListener('pointermove', onMove);
      dom.style.cursor = 'default';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPicking]);

  // Click-to-pick. Uses the browser-level `click` event so we don't
  // have to track pointerdown/pointerup state across handler invocations
  // — the browser dispatches `click` atomically and only when the
  // pointer hasn't moved past its built-in threshold. Eliminates the
  // mid-gesture re-bind race that caused first clicks to drop.
  useEffect(() => {
    if (!isPicking) return;
    const dom = renderer.domElement;

    const onClick = async (e: MouseEvent) => {
      if (e.button !== 0) return;
      if (Date.now() < suppressClickUntilRef.current) return;
      const m = modeRef.current;
      const rect = dom.getBoundingClientRect();

      // Snap-to-first detection BEFORE the draggingRef bail (R3F's
      // marker pointerdown sets draggingRef and that handler's own
      // onUp fires after this canvas onUp). Done at click time, not
      // read from `mode.snapToFirst`.
      if (m.currentPart.length >= 3) {
        const first = m.currentPart[0];
        const firstVec = new THREE.Vector3(first[0], first[1], first[2]);
        const firstScreen = worldToScreenPx(firstVec, rect);
        if (firstScreen) {
          const ddx = e.clientX - rect.left - firstScreen.x;
          const ddy = e.clientY - rect.top - firstScreen.y;
          if (Math.sqrt(ddx * ddx + ddy * ddy) <= SNAP_PX_THRESHOLD) {
            if (!isRingSimple2D(projectXY(m.currentPart))) return;
            draggingRef.current = null;
            setMode((prev) => ({
              ...prev,
              parts: [...prev.parts, prev.currentPart],
              currentPart: [],
              snapToFirst: false,
            }));
            setHoverPoint(null);
            setCandidateInvalid(false);
            return;
          }
        }
      }

      if (draggingRef.current) return;

      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(new THREE.Vector2(x, y), camera);
      const hit = pickSurface(
        renderer,
        camera,
        raycaster,
        pcoTargetsRef.current,
        meshTargetsRef.current,
      );
      if (!hit) return;

      const candidate: [number, number, number] = [hit.position.x, hit.position.y, hit.position.z];
      if (m.currentPart.length >= 2) {
        const next = [...m.currentPart, candidate];
        if (!isPolylineSimple2D(projectXY(next))) return;
      }
      setMode((prev) => ({ ...prev, currentPart: [...prev.currentPart, candidate] }));
      setHoverPoint(null);
      setCandidateInvalid(false);
    };

    dom.addEventListener('click', onClick);
    return () => dom.removeEventListener('click', onClick);
    // Re-binds only when the tool starts/stops. All mutable inputs the
    // handlers need are read through refs to avoid mid-gesture re-binds.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPicking]);

  // Cancel via right-click during picking.
  useEffect(() => {
    if (!isPicking) return;
    const dom = renderer.domElement;
    const onCtx = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setMode({ ...DEFAULT_ADD_POLYGON_MODE });
    };
    dom.addEventListener('contextmenu', onCtx);
    return () => dom.removeEventListener('contextmenu', onCtx);
  }, [isPicking, renderer, setMode]);

  // Double-click to finalize. Same shape as AddLineTool's dblclick: commit
  // any valid current ring (≥3 verts AND simple) plus committed parts and
  // transition to adjusting. If the current ring is invalid we still
  // commit the previously-finalized parts.
  useEffect(() => {
    if (!isPicking) return;
    const dom = renderer.domElement;
    const onDbl = (e: MouseEvent) => {
      e.preventDefault();
      setMode((prev) => {
        if (prev.phase !== 'picking') return prev;
        const validCurrent = prev.currentPart.length >= 3 && isRingSimple2D(projectXY(prev.currentPart));
        const allParts = validCurrent ? [...prev.parts, prev.currentPart] : prev.parts;
        if (allParts.length === 0) return prev;
        return {
          ...prev,
          parts: allParts,
          currentPart: [],
          snapToFirst: false,
          phase: 'adjusting',
        };
      });
    };
    dom.addEventListener('dblclick', onDbl);
    return () => dom.removeEventListener('dblclick', onDbl);
  }, [isPicking, renderer, setMode]);

  /**
   * Vertex drag on a camera-perpendicular plane; snaps to the picked
   * surface on release. Distinguishes click-vs-drag by movement
   * threshold so a click on the first vertex (no drag) closes the ring
   * instead of silently moving the vertex by 0 px and swallowing the
   * canvas click — the previous behaviour read as "first point moves
   * instead of closing ring" because R3F's pointer dispatch hits the
   * marker before the canvas pointerup, then click suppression was
   * blocking the canvas snap-to-first.
   */
  const startVertexDrag = (e: ThreeEvent<PointerEvent>, address: VertexAddress) => {
    if (!enabled) return;
    e.stopPropagation();

    const camDir = camera.getWorldDirection(new THREE.Vector3());
    const point = e.point.clone();
    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(camDir, point);
    draggingRef.current = { address, plane };
    const startX = e.clientX;
    const startY = e.clientY;
    const DRAG_THRESHOLD_PX = 5;
    let didDrag = false;

    const dom = renderer.domElement;

    const writeVertex = (next: [number, number, number]) => {
      setMode((prev) => {
        if (address.kind === 'current') {
          if (address.vertexIdx >= prev.currentPart.length) return prev;
          const updated = prev.currentPart.slice();
          updated[address.vertexIdx] = next;
          return { ...prev, currentPart: updated };
        }
        if (address.partIdx >= prev.parts.length) return prev;
        const part = prev.parts[address.partIdx];
        if (address.vertexIdx >= part.length) return prev;
        const updatedPart = part.slice();
        updatedPart[address.vertexIdx] = next;
        const updatedParts = prev.parts.slice();
        updatedParts[address.partIdx] = updatedPart;
        return { ...prev, parts: updatedParts };
      });
    };

    const onMove = (ev: PointerEvent) => {
      ev.stopPropagation();
      if (!didDrag) {
        const dx = ev.clientX - startX;
        const dy = ev.clientY - startY;
        if (Math.sqrt(dx * dx + dy * dy) <= DRAG_THRESHOLD_PX) return;
        didDrag = true;
      }
      const rect = dom.getBoundingClientRect();
      const x = ((ev.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((ev.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(new THREE.Vector2(x, y), camera);
      const hit = new THREE.Vector3();
      if (!raycaster.ray.intersectPlane(plane, hit)) return;
      writeVertex([hit.x, hit.y, hit.z]);
    };

    const onUp = (ev: PointerEvent) => {
      window.removeEventListener('pointermove', onMove, true);
      window.removeEventListener('pointerup', onUp);
      draggingRef.current = null;

      if (didDrag) {
        // Real drag: snap final position to the picked surface and
        // suppress the canvas click handler so it doesn't append a vertex.
        suppressClickUntilRef.current = Date.now() + 200;
        const rect = dom.getBoundingClientRect();
        const x = ((ev.clientX - rect.left) / rect.width) * 2 - 1;
        const y = -((ev.clientY - rect.top) / rect.height) * 2 + 1;
        raycaster.setFromCamera(new THREE.Vector2(x, y), camera);
        const hit = pickSurface(renderer, camera, raycaster, pcoTargetsRef.current, meshTargetsRef.current);
        if (hit) writeVertex([hit.position.x, hit.position.y, hit.position.z]);
        return;
      }

      // No drag — this was a click on a vertex marker. For the first
      // vertex of the current part with ≥3 picked, that means "close
      // the ring". For any other vertex, just absorb the click.
      suppressClickUntilRef.current = Date.now() + 200;
      if (address.kind === 'current' && address.vertexIdx === 0) {
        setMode((prev) => {
          if (prev.currentPart.length < 3) return prev;
          if (!isRingSimple2D(projectXY(prev.currentPart))) return prev;
          return {
            ...prev,
            parts: [...prev.parts, prev.currentPart],
            currentPart: [],
            snapToFirst: false,
          };
        });
      }
    };

    window.addEventListener('pointermove', onMove, true);
    window.addEventListener('pointerup', onUp);
  };

  if (!enabled) return null;

  // Floating origin — see AddLineTool for the rationale. Subtract a
  // stable session origin from every coordinate before sending it to
  // the GPU, then add it back via the parent group's matrix. Kills the
  // float32-precision jitter that was visible at UTM scale.
  const origin: [number, number, number] =
    mode.currentPart.length > 0
      ? mode.currentPart[0]
      : mode.parts.length > 0 && mode.parts[0].length > 0
        ? mode.parts[0][0]
        : [0, 0, 0];
  const sub = (x: number, y: number, z: number): THREE.Vector3 =>
    new THREE.Vector3(x - origin[0], y - origin[1], z - origin[2]);
  const subVec = (v: THREE.Vector3): THREE.Vector3 => sub(v.x, v.y, v.z);

  const previousRings = mode.parts.map((p) => p.map(([x, y, z]) => sub(x, y, z)));
  const currentVerts = mode.currentPart.map(([x, y, z]) => sub(x, y, z));
  const hoverRel = hoverPoint ? subVec(hoverPoint) : null;
  const candidateColor = candidateInvalid ? ERROR_COLOR : POLYGON_COLOR;
  // Trailing ghost segment: just the single edge from the last picked vertex
  // to the cursor (or back to the first vertex when snap-to-first is active).
  // We render it as its own Line rather than extending the committed polyline
  // so the dashed pattern starts cleanly at the segment origin instead of
  // continuing from the cumulative line distance — short hover segments were
  // landing inside a gap of the dash cycle and rendering invisible.
  const trailingSegment: [THREE.Vector3, THREE.Vector3] | null = !isPicking
    ? null
    : mode.snapToFirst && currentVerts.length >= 3
      ? [currentVerts[currentVerts.length - 1], currentVerts[0]]
      : hoverRel && currentVerts.length >= 1
        ? [currentVerts[currentVerts.length - 1], hoverRel]
        : null;
  const previousColor = isPicking ? POLYGON_COLOR_PREV : POLYGON_COLOR;

  // Origin-relative coordinates for the segment-label component.
  const partsRel: [number, number, number][][] = mode.parts.map((p) =>
    p.map(([x, y, z]) => [x - origin[0], y - origin[1], z - origin[2]] as [number, number, number]),
  );
  const currentPartRel: [number, number, number][] = mode.currentPart.map(
    ([x, y, z]) => [x - origin[0], y - origin[1], z - origin[2]] as [number, number, number],
  );

  return (
    <group position={origin}>
      {/* Previously committed rings (closed) */}
      {previousRings.map((ring, i) =>
        ring.length >= 3 ? (
          <Line
            key={`poly-prev-${i}`}
            points={[...ring, ring[0]]}
            color={previousColor}
            lineWidth={isPicking ? 2.5 : 3}
            transparent
            opacity={isPicking ? 0.7 : 0.95}
            depthTest={false}
          />
        ) : null,
      )}

      {/* Vertices for committed rings — draggable. */}
      {previousRings.map((ring, partIdx) =>
        ring.map((v, vertexIdx) => (
          <CrosshairMarker
            key={`pp-vert-${partIdx}-${vertexIdx}`}
            position={v}
            color={POLYGON_COLOR}
            screenSize={0.012}
            opacity={isPicking ? 0.6 : 0.9}
            onPointerDown={(e) => startVertexDrag(e, { kind: 'committed', partIdx, vertexIdx })}
          />
        )),
      )}

      {/* Current part — solid polyline so far. */}
      {currentVerts.length >= 2 && (
        <Line points={currentVerts} color={POLYGON_COLOR} lineWidth={3} transparent opacity={0.95} depthTest={false} />
      )}

      {/* Vertices for the in-progress current part — draggable, with the
          first vertex enlarged + glowing green when snap-eligible. */}
      {currentVerts.map((v, vertexIdx) => {
        const isFirstSnap = vertexIdx === 0 && mode.snapToFirst && currentVerts.length >= 3;
        return (
          <CrosshairMarker
            key={`cp-vert-${vertexIdx}`}
            position={v}
            color={isFirstSnap ? SNAP_HIGHLIGHT_COLOR : POLYGON_COLOR}
            screenSize={isFirstSnap ? 0.022 : 0.014}
            onPointerDown={(e) => startVertexDrag(e, { kind: 'current', vertexIdx })}
          />
        );
      })}

      {/* Live trailing ghost edge — single segment from last vertex to
          cursor (or to first vertex when snap-to-first is active). */}
      {trailingSegment && <TrailingSegmentLine points={trailingSegment} color={candidateColor} />}

      {/* Cursor preview marker — hidden when snapping (the first vertex is the visual). */}
      {isPicking && hoverRel && !mode.snapToFirst && !draggingRef.current && (
        <CrosshairMarker
          position={hoverRel}
          color={candidateColor}
          screenSize={0.012}
          opacity={0.55}
          sphereOpacity={0.4}
        />
      )}

      {/* Per-segment length labels. While snap-to-first is active we also
          label the closing edge (current[last] → current[0]) so the user
          sees what closing the ring will produce. */}
      <SegmentLabels
        parts={partsRel}
        current={currentPartRel}
        cursor={isPicking && !mode.snapToFirst ? hoverRel : null}
        closeCurrentRing={isPicking && mode.snapToFirst}
      />
    </group>
  );
};
